import { Download, Trash2, CheckCircle, Clock, XCircle } from "lucide-react";

export default function InvoicesTable({
  data,
  downloadInvoice,
  onDelete,
  onStatusChange,
  isLoading,
}) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500 text-sm">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <div className="text-gray-400 mb-2">
          <Clock size={48} className="mx-auto" />
        </div>
        <p className="text-gray-500 text-lg">No invoices found</p>
        <p className="text-gray-400 text-sm mt-1">Create your first invoice to get started</p>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 0:
        return <Clock className="text-yellow-500" size={18} title="Pending" />;
      case 1:
        return <CheckCircle className="text-green-600" size={18} title="Paid" />;
      case 2:
        return <XCircle className="text-red-500" size={18} title="Cancelled" />;
      default:
        return <Clock className="text-gray-400" size={18} title="Unknown" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 0:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 1:
        return `${baseClasses} bg-green-100 text-green-800`;
      case 2:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Month-Year
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Invoice #
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Publisher
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Paid Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((invoice, index) => (
              <tr
                key={invoice.id}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="px-6 py-4 whitespace-nowrap">
  <span className="text-sm font-medium text-gray-900">
    {invoice.invoiceDate
      ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }).replace(" ", "-") // converts "Oct 2025" → "Oct-2025"
      : "-"}
  </span>
</td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    {invoice.invoiceNumber}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3">
                      {invoice.publisherName?.charAt(0) || 'P'}
                    </div>
                    <span className="text-sm text-gray-900 font-medium">
                      {invoice.publisherName}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(invoice.publisherRevenue)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">
                    {invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString() : "-"}
                  </span>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(invoice.status)}
                      <span className={getStatusBadge(invoice.status)}>
                        {invoice.status === 0 && 'Pending'}
                        {invoice.status === 1 && 'Paid'}
                        {invoice.status === 2 && 'Cancelled'}
                      </span>
                    </div>
                    <select
                      value={invoice.status}
                      onChange={(e) =>
                        onStatusChange(invoice.id, parseInt(e.target.value))
                      }
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value={0}>Pending</option>
                      <option value={1}>Paid</option>
                      <option value={2}>Cancelled</option>
                    </select>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => downloadInvoice(invoice)}
                      className="inline-flex items-center justify-center p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                      title="Download Invoice"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => onDelete(invoice)}
                      className="inline-flex items-center justify-center p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                      title="Delete Invoice"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Optional: Table footer with summary */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing <span className="font-medium">{data.length}</span> invoices
          </p>
          <div className="text-sm text-gray-600">
            Total Revenue: <span className="font-semibold text-gray-900">
              {formatCurrency(data.reduce((sum, invoice) => sum + invoice.publisherRevenue, 0))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}